import React from 'react';
import { useTranslation } from 'react-i18next';
import { images } from '../../components/shared/assets';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { usePageContent, useArtImmersions, useSiteSettings } from '../../hooks/useContentful';
import { ContentfulText, ContentfulMultilineText } from '../../components/Contentful';
import {
  ArtDiptych,
  ArticleGallerySubheader,
  ArticleGallery,
  FeathersVideo,
  FeathersVideoFooter
} from './components';
import './ArtImmersionsPage.css';

const ArtImmersionsPage: React.FC = () => {
  const { t } = useTranslation();
  
  // Fetch content from Contentful
  const { data: pageContentData } = usePageContent('art-immersions');
  const { data: artImmersionsData } = useArtImmersions();
  const { data: siteSettingsData } = useSiteSettings();
  const pageContent = pageContentData?.[0]?.fields as any;
  const siteSettings = siteSettingsData?.[0]?.fields as any;
  
  // Use Contentful art immersions if available, otherwise fall back to static data
  const contentfulImmersions = artImmersionsData?.map((immersion: any) => ({
    id: immersion.sys.id,
    title: immersion.fields.title,
    location: immersion.fields.location,
    date: immersion.fields.date ? new Date(String(immersion.fields.date)).toLocaleDateString() : '',
    description: immersion.fields.description,
    image: immersion.fields.featuredImage?.fields?.file?.url ? `https:${immersion.fields.featuredImage.fields.file.url}` : '',
    hasVideo: immersion.fields.hasVideo || false
  })) || [];
  
  // Static art immersions data as fallback
  const staticArtImmersions = [
    {
      id: 'ingrid-weyland-topographies',
      title: t('pages/artImmersions:artists.ingridWeyland.title'),
      location: t('pages/artImmersions:artists.ingridWeyland.location'),
      date: '03.02.2022',
      description: t('pages/artImmersions:artists.ingridWeyland.description'),
      image: "https://images.ctfassets.net/hz1mviiqoqof/7m206lCUA2JGr4gD2GXwOA/76f0c0f0278e2910f8a37ad95374c6b9/art-ingrid-weyland.png",
      hasVideo: false
    },
    {
      id: 'teresa-marcaida-nature-art',
      title: t('pages/artImmersions:artists.teresaMarcaida.title'),
      location: t('pages/artImmersions:artists.teresaMarcaida.location'),
      date: '03.02.2022',
      description: t('pages/artImmersions:artists.teresaMarcaida.description'),
      image: "https://images.ctfassets.net/hz1mviiqoqof/YUYkDXP8n3MhkyfLfvRgL/55ad9dcbb368532f7fd5b2260ff524c6/art-teresa-marcaida.png",
      hasVideo: true
    },
    {
      id: 'rainer-gross-double-takes',
      title: t('pages/artImmersions:artists.rainerGross.title'),
      location: t('pages/artImmersions:artists.rainerGross.location'),
      date: '03.02.2022',
      description: t('pages/artImmersions:artists.rainerGross.description'),
      image: "https://images.ctfassets.net/hz1mviiqoqof/2LGBwJ5lhRPqrO2hnlKQ41/80ca464571d58eae781a674d4732e35c/art-rainer-gross.png",
      hasVideo: true
    },
    {
      id: 'iliana-diaz-portales',
      title: t('pages/artImmersions:artists.ilianaDiaz.title'),
      location: t('pages/artImmersions:artists.ilianaDiaz.location'),
      date: '03.02.2022',
      description: t('pages/artImmersions:artists.ilianaDiaz.description'),
      image: "https://images.ctfassets.net/hz1mviiqoqof/3YJfvRUFSEQMhu09O7TTNn/f9e168bf105d99df142d449221e6ceea/art-iliana-diaz.png",
      hasVideo: true
    },
    {
      id: 'andres-paredes-trama',
      title: t('pages/artImmersions:artists.andresParedes.title'),
      location: t('pages/artImmersions:artists.andresParedes.location'),
      date: '02.11.2023',
      description: t('pages/artImmersions:artists.andresParedes.description'),
      image: images.artIngridWeyland || '',
      hasVideo: false
    },
    {
      id: 'cecilia-nigro-cuando',
      title: t('pages/artImmersions:artists.ceciliaNigro.title'),
      location: t('pages/artImmersions:artists.ceciliaNigro.location'),
      date: '02.01.2024',
      description: t('pages/artImmersions:artists.ceciliaNigro.description'),
      image: images.artTeresaMarcaida || '',
      hasVideo: false
    },
    {
      id: 'florencia-echeverria-selva',
      title: t('pages/artImmersions:artists.florenciaEcheverria.title'),
      location: t('pages/artImmersions:artists.florenciaEcheverria.location'),
      date: '20.06.2024',
      description: t('pages/artImmersions:artists.florenciaEcheverria.description'),
      image: images.artRainerGross || '',
      hasVideo: true
    }
  ];
  
  const artImmersions = contentfulImmersions.length > 0 ? contentfulImmersions : staticArtImmersions;


  return (
    <div className="art-immersions-page">
      {/* Navigation Header */}
      <SmartNavigation 
        defaultVariant="transparent"
        scrolledVariant="light"
        autoSwitchOnScroll={true}
        forceWhiteAssets={true}
      />
      
      {/* Orange vertical line accent */}
      <div className="vertical-accent-line" />
      
      {/* 1. Diptych Hero Section */}
      <ArtDiptych
        leftContent={{
          text: pageContent?.diptychText || t('pages/artImmersions:hero.text'),
          backgroundColor: 'var(--color-iguazu)'
        }}
        rightContent={{
          video: "https://videos.ctfassets.net/hz1mviiqoqof/4LacpK38X9lHw3prjxqcft/2c13736c240d66a4495ec4ba904dd4b2/parrot_4_art_imersions_masthead_video.mp4",
          alt: 'Parrot art immersions video'
        }}
      />

      {/* 2. Article Gallery Subheader */}
      <ArticleGallerySubheader
        contentfulText={pageContent?.subheaderText}
        fallbackText={t('pages/artImmersions:subheader.text')}
      />

      {/* 3. Article Gallery with Filters + Pagination */}
      <ArticleGallery
        immersions={artImmersions}
        itemsPerPage={4}
      />

      {/* 4. Feathers Video */}
      <FeathersVideo
        videoSrc={images.filmVideo1}
        autoPlay={true}
      />

      {/* 5. Feathers Video Footer */}
      <FeathersVideoFooter
        quote={pageContent?.feathersQuote || t('pages/artImmersions:footer.quote')}
        linkText={pageContent?.feathersLinkText || siteSettings?.globalCTAs?.learnMore || t('pages/artImmersions:footer.button')}
        linkUrl="/our-story"
      />

      {/* 6. Instagram Section - matching Film Collection page */}
      <InstagramSection />

      {/* 7. Footer */}
      <Footer variant="default" />
    </div>
  );
};

export default ArtImmersionsPage;